import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useMount } from 'react-use';
import * as analytics from '@eb/site-analytics';
import { collectionsDuck } from '@eb/redux-destination';
import { LoadingCards } from '@eb/eds-event-card';

import {
    setHoverEventTarget as setHoverEventTargetAction,
    setGoogleMapsLoaded as setGoogleMapsLoadedAction,
} from '../../../../redux/actions/ui';
import { trackEventClickInternal as trackEventClickInternalAction } from '../../../../redux/actions/analytics';
import { getEventExpansions as getEventExpansionsAction } from '../../../../redux/actions/expansions';

import { getSearchContent, getSearchSection } from '../../../../utils/general';
import { hasEventResults } from '../../utils/events';
import { addSearchIdToGaAction } from '../../utils/analytics';

import { SearchEventCardStyleSwitcher } from '../../../../components/EventCard';
import {
    GA_SEARCH_CATEGORY,
    SEARCH_SECTION,
    SEARCH_EVENT_NAVIGATION,
} from '../../../../constants/analytics';

import { SearchHeaderContainer } from '../SearchHeaderContainer';

const { trackEventFromState, trackEventFromStateOnPageUnload } = analytics;

const {
    actions: {
        addOrRemoveEventToCollection: addOrRemoveEventToCollectionAction,
        createCollection: createCollectionAction,
    },
    selectors: { getFormattedCollections },
} = collectionsDuck;

import './SearchResultsPanelContainer.scss';

const LIST_TYPE = 'list';

const SearchResults = (props) => {
    useMount(() => {
        const checkForDedup = ({ dedup }) =>
            dedup && typeof dedup.count === 'number' && dedup.count > 1;
        const hasDedupedEvent = props.content.some((event) =>
            checkForDedup(event),
        );

        if (hasDedupedEvent) {
            analytics.trackAnalyticsEvent({
                category: 'search',
                action: 'DeDupedResultsShown',
            });
        }
    });

    return (
        <ul className="search-main-content__events-list">
            {props.content.map(({ id, ...cardProps }, index) => (
                <li key={id}>
                    <div
                        className="search-event-card-wrapper"
                        onMouseEnter={props.onMouseOverCard.bind(null, id)}
                        onFocus={props.onMouseOverCard.bind(null, id)}
                        onMouseLeave={props.onMouseOutCard}
                        onBlur={props.onMouseOutCard}
                        role="presentation"
                    >
                        <SearchEventCardStyleSwitcher
                            id={id}
                            trackClick={props.trackClick.bind(null, index, id)}
                            type={LIST_TYPE}
                            isAuthenticated={props.isAuthenticated}
                            containerSpacingClasses={
                                props.isMobile ? '' : 'eds-l-pad-all-4'
                            }
                            trackClickInternal={props.trackClickInternal.bind(
                                null,
                                index,
                            )}
                            trackGAEvent={props.trackGAEvent}
                            section={SEARCH_SECTION}
                            featureFlags={props.featureFlags}
                            position={index + 1}
                            shouldShowDebugOutput={props.shouldShowDebugOutput}
                            history={props.history}
                            {...cardProps}
                        />
                    </div>
                </li>
            ))}
        </ul>
    );
};

/**
 * Depending on the loading state of the application
 * will render the search results themselves or the
 * the appropriate loading state.
 * */
export const SearchResultsController = ({ isLoading, isMobile, ...props }) => {
    let component = (
        <div className="search-main-content">
            <LoadingCards style={isMobile ? 'list-square' : 'list'} />
        </div>
    );

    if (!isLoading) {
        component = (
            <div className="search-main-content">
                {props.results.map(({ content, type }) => {
                    const key = `search_${type}`;
                    const emptyResults = <div key={key} />;

                    if (!content) {
                        return emptyResults;
                    }

                    return (
                        <SearchResults {...props} content={content} key={key} />
                    );
                })}
            </div>
        );
    }

    return component;
};

const SearchResultSection = ({
    searchResultProps,
    isLoading,
    hasEventResults,
    isMobile,
}) => {
    let component = <SearchResultsController {...searchResultProps} />;

    if (isMobile) {
        component = (
            <SearchResultsController {...searchResultProps} isMobile={true} />
        );
    }

    if (!isLoading && !hasEventResults) {
        component = null;
    }

    return component;
};

export class ResultsPanel extends React.PureComponent {
    static propTypes = {
        results: PropTypes.arrayOf(PropTypes.object),
        isAuthenticated: PropTypes.bool,
        onTrackEventClickInternal: PropTypes.func,
        hasEventResults: PropTypes.bool,
        trackGAEvent: PropTypes.func,
        trackGAEventOnPageUnload: PropTypes.func,
        // TODO: Add proptype shape
        allEventsUrl: PropTypes.string,
        onGoogleMapsLoaded: PropTypes.func,
    };

    componentDidMount() {
        this.props.getEventExpansions();
    }

    _handleTrackClick = (index, eventId, action, organizerFollowerCount) => {
        const { trackGAEvent } = this.props;

        trackGAEvent({
            action,
            category: GA_SEARCH_CATEGORY,
            label: eventId,
            dimensions: {
                eventId,
            },
            metrics: {
                searchRank: index,
                organizerFollowerCount: organizerFollowerCount || 0,
            },
        });
    };

    _handleTrackClickInternal = (index, id, ticketsBy) => {
        const { onTrackEventClickInternal } = this.props;

        const trackingData = {
            clickRank: index,
            navigation: SEARCH_EVENT_NAVIGATION,
            itemId: id,
        };

        onTrackEventClickInternal(trackingData, ticketsBy);
    };

    _handleMouseOverCard = (eid) => {
        this.props.setHoverEventTarget(eid);
    };

    _handleMouseOutCard = () => {
        this.props.setHoverEventTarget(null);
    };

    render() {
        const {
            allEventsUrl,
            featureFlags,
            hasEventResults,
            history,
            isAuthenticated,
            isLoading,
            onGoogleMapsLoaded,
            results,
            shouldShowDebugOutput,
            trackGAEvent,
            trackGAEventOnPageUnload,
        } = this.props;

        const searchResultProps = {
            featureFlags,
            history,
            isAuthenticated,
            isLoading,
            onMouseOutCard: this._handleMouseOutCard,
            onMouseOverCard: this._handleMouseOverCard,
            results,
            shouldShowDebugOutput,
            trackClick: this._handleTrackClick,
            trackClickInternal: this._handleTrackClickInternal,
            trackGAEvent,
        };

        return (
            <div className="search-results-panel-content">
                <SearchHeaderContainer
                    onGoogleMapsLoaded={onGoogleMapsLoaded}
                    history={this.props.history}
                />
                <SearchResultSection
                    allEventsUrl={allEventsUrl}
                    featureFlags={featureFlags}
                    hasEventResults={hasEventResults}
                    isAuthenticated={isAuthenticated}
                    isLoading={isLoading}
                    searchResultProps={searchResultProps}
                    trackGAEvent={trackGAEvent}
                    trackGAEventOnPageUnload={trackGAEventOnPageUnload}
                />
            </div>
        );
    }
}

export const _mapStateToProps = (state, ownProps) => {
    const section = getSearchSection(state.location.isOnline);
    const results = getSearchContent(
        state.content.entities,
        state.pagination,
        state.currencyFormat,
        section,
    );

    return {
        results,
        history: ownProps.history,
        hasEventResults: hasEventResults(results),
        isAuthenticated: state.auth.isAuthenticated,
        isLoading: state.pagination.loading,
        featureFlags: state.app.featureFlags,
        shouldShowDebugOutput: state.admin.shouldShowDebugOutput,
        trackGAEvent: (action) =>
            trackEventFromState(state, addSearchIdToGaAction(action, state)),
        trackGAEventOnPageUnload: (action) =>
            trackEventFromStateOnPageUnload(
                state,
                addSearchIdToGaAction(
                    {
                        ...action,
                        category: GA_SEARCH_CATEGORY,
                    },
                    state,
                ),
            ),
    };
};

const _mapDispatchToProps = (dispatch) => ({
    onGoogleMapsLoaded: (...args) =>
        dispatch(setGoogleMapsLoadedAction(...args)),
    setHoverEventTarget: (...args) =>
        dispatch(setHoverEventTargetAction(...args)),
    onTrackEventClickInternal: (...args) =>
        dispatch(trackEventClickInternalAction(...args)),
    getEventExpansions: (...args) =>
        dispatch(getEventExpansionsAction(...args)),
});

export const SearchResultsPanelContainer = connect(
    _mapStateToProps,
    _mapDispatchToProps,
)(ResultsPanel);
